import React from 'react';
import {
  Modal,
  Paper,
  Typography,
  Box,
  Chip,
  IconButton,
  CircularProgress,
} from '@mui/material';
import {
  Timeline,
  TimelineItem,
  TimelineSeparator,
  TimelineConnector,
  TimelineContent,
  TimelineOppositeContent,
  TimelineDot,
} from '@mui/lab';
import {
  Close as CloseIcon,
  InstallMobile as InstallIcon,
  RemoveCircle as RemoveIcon,
  Edit as EditIcon,
  AddCircleOutline as CreateIcon,
} from '@mui/icons-material';
import { useQuery } from '@tanstack/react-query';
import axios from '../../api/axiosConfig';
import { formatDateOnly } from '../../utils/dateUtils';
import { useAuth } from '../../hooks/useAuth';

const fetchDeviceHistory = async (id) => {
  const { data } = await axios.get(`/devices/${id}/history`);
  return data;
};

const getIcon = (action) => {
  switch (action) {
    case 'install': return <InstallIcon />;
    case 'remove': return <RemoveIcon />;
    case 'update': return <EditIcon />;
    case 'create': return <CreateIcon />;
    default: return <EditIcon />;
  }
};

const getColor = (action) => {
  switch (action) {
    case 'install': return 'success';
    case 'remove': return 'error';
    case 'update': return 'warning';
    case 'create': return 'info';
    default: return 'default';
  }
};

const getActionLabel = (action) => {
  switch (action) {
    case 'install': return 'Установлено';
    case 'remove': return 'Снято';
    case 'update': return 'Изменено';
    case 'create': return 'Создано';
    default: return 'Действие';
  }
};

const DeviceHistoryModal = ({ open, deviceId, onClose }) => {
  const { user } = useAuth();
  const { data: history, isLoading, error } = useQuery({
    queryKey: ['deviceHistory', deviceId],
    queryFn: () => fetchDeviceHistory(deviceId),
    enabled: !!deviceId && open,
  });

  return (
    <Modal
      open={open}
      onClose={onClose}
      sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}
    >
      <Paper
        sx={{
          width: '90%',
          maxWidth: 800,
          height: '70%',
          overflow: 'auto',
          position: 'relative',
          p: 3,
          borderRadius: 2,
        }}
      >
        <IconButton
          onClick={onClose}
          sx={{ position: 'absolute', right: 8, top: 8, zIndex: 1 }}
        >
          <CloseIcon />
        </IconButton>

        <Typography variant="h5" gutterBottom fontWeight={600}>
          История устройства
        </Typography>
        <Typography variant="body2" color="textSecondary" sx={{ mb: 3 }}>
          Все изменения: установка, снятие, редактирование полей
        </Typography>

        {isLoading && (
          <Box display="flex" justifyContent="center" alignItems="center" py={8}>
            <CircularProgress />
          </Box>
        )}

        {error && (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <Typography color="error">
              Ошибка загрузки истории: {error.message}
            </Typography>
          </Box>
        )}

        {history && history.length === 0 && (
          <Box sx={{ textAlign: 'center', py: 8 }}>
            <Typography color="textSecondary" gutterBottom>
              История отсутствует.
            </Typography>
            <Typography variant="caption" color="textSecondary">
              Записи появятся при создании, установке или изменении устройства.
            </Typography>
          </Box>
        )}

        {history && history.length > 0 && (
          <Timeline position="alternate">
            {history.map((event, idx) => (
              <TimelineItem key={idx}>
                <TimelineOppositeContent color="textSecondary">
                  {formatDateOnly(
                    event.changedAt || event.installedAt || event.removedAt,
                    user?.settings?.timezone || 'UTC',
                    user?.settings?.dateFormat || 'dd/MM/yyyy'
                  )}
                  <Typography variant="caption" display="block" color="textSecondary">
                    {event.changedAt && new Date(event.changedAt).toLocaleTimeString([], {
                      hour: '2-digit',
                      minute: '2-digit',
                    })}
                  </Typography>
                </TimelineOppositeContent>

                <TimelineSeparator>
                  <TimelineDot color={getColor(event.action)}>
                    {getIcon(event.action)}
                  </TimelineDot>
                  {idx < history.length - 1 && <TimelineConnector />}
                </TimelineSeparator>

                <TimelineContent>
                  <Paper
                    elevation={2}
                    sx={{
                      p: 2,
                      bgcolor: 'background.paper',
                      borderRadius: 2,
                      transition: 'transform 0.2s',
                      '&:hover': { transform: 'translateY(-2px)', boxShadow: 3 },
                    }}
                  >
                    <Typography variant="h6" gutterBottom>
                      {getActionLabel(event.action)}
                    </Typography>

                    {event.action === 'update' && event.field && (
                      <Typography variant="body2" color="textSecondary" paragraph>
                        <strong>Поле:</strong> {event.field}<br />
                        <strong>Было:</strong> {event.oldValue || 'пусто'}<br />
                        <strong>Стало:</strong> {event.newValue || 'пусто'}
                      </Typography>
                    )}

                    {event.action === 'install' && event.vehicleId && (
                      <Typography variant="body2" color="textSecondary" paragraph>
                        <strong>Установлено на ТС:</strong> {event.vehicleId.licensePlate || event.vehicleId}
                      </Typography>
                    )}

                    {event.action === 'remove' && event.vehicleId && (
                      <Typography variant="body2" color="textSecondary" paragraph>
                        <strong>Снято с ТС:</strong> {event.vehicleId.licensePlate || event.vehicleId}
                      </Typography>
                    )}

                    {event.notes && (
                      <Box sx={{ mt: 1, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                        <Chip label="Примечание" size="small" variant="outlined" />
                        <Typography variant="caption" color="textSecondary">
                          {event.notes}
                        </Typography>
                      </Box>
                    )}

                    {event.changedBy && (
                      <Typography variant="caption" display="block" color="textSecondary" sx={{ mt: 1 }}>
                        <strong>Кто изменил:</strong> {event.changedBy.fullName}
                      </Typography>
                    )}
                  </Paper>
                </TimelineContent>
              </TimelineItem>
            ))}
          </Timeline>
        )}
      </Paper>
    </Modal>
  );
};

export default DeviceHistoryModal;